import { baseApi } from '@shared/api/baseApi';
import type { ProductDto } from './productApi';

export interface ProductImageDto { id: string; url: string; alt: string; }
export interface ProductShopDto {
  id: string; name: string; slug: string; logoUrl: string | null; isPlatformShop: boolean;
}
export interface ProductDetailDto {
  product: ProductDto & { images: ProductImageDto[]; canManageImages: boolean; instructions?: string; refundPolicy?: string; deliveryPolicy?: string; shop: ProductShopDto | null };
}
export interface ProductReviewDto {
  id: string; rating: number; body: string; authorName: string; createdAt: string;
}
export interface ProductReviewsDto {
  reviews: ProductReviewDto[];
  summary: { average: number | null; count: number };
  canReview: boolean;
  myReview: ProductReviewDto | null;
}

const productExperienceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    recordProductView: builder.mutation<unknown, { slug: string; visitorId: string; eventId: string }>({ query: ({ slug, visitorId, eventId }) => ({ url: `/products/${encodeURIComponent(slug)}/views`, method: 'POST', body: { visitorId, eventId } }) }),
    getProductDetail: builder.query<ProductDetailDto, string>({
      query: (slug) => `/products/${encodeURIComponent(slug)}`,
      providesTags: ['Product'],
    }),
    getProductReviews: builder.query<ProductReviewsDto, string>({
      query: (slug) => `/products/${encodeURIComponent(slug)}/reviews`,
      // Completing a purchase also refreshes the buyer's review eligibility.
      providesTags: ['Product', 'Order'],
    }),
    saveProductReview: builder.mutation<{ review: ProductReviewDto }, { slug: string; rating: number; body: string }>({
      query: ({ slug, ...body }) => ({ url: `/products/${encodeURIComponent(slug)}/reviews`, method: 'PUT', body }),
      invalidatesTags: ['Product'],
    }),
    uploadProductImage: builder.mutation<{ image: ProductImageDto }, { slug: string; content: string; alt?: string }>({
      query: ({ slug, ...body }) => ({ url: `/products/${encodeURIComponent(slug)}/images`, method: 'POST', body }),
      invalidatesTags: ['Product'],
    }),
    deleteProductImage: builder.mutation<unknown, { slug: string; imageId: string }>({
      query: ({ slug, imageId }) => ({ url: `/products/${encodeURIComponent(slug)}/images/${encodeURIComponent(imageId)}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),
  }),
});

export const {
  useRecordProductViewMutation, useGetProductDetailQuery, useGetProductReviewsQuery, useSaveProductReviewMutation,
  useUploadProductImageMutation, useDeleteProductImageMutation,
} = productExperienceApi;
